import * as THREE from "three";
import { TransformControls } from "three/examples/jsm/controls/TransformControls.js";
import { store } from "../core/store.js";
import { markers } from "./screenMarker.js";
import { LabelMesh } from "./labelMesh.js";
import { createWorldLabelRenderer } from "./worldLabelRenderer.js";
import { cleanupGizmo } from "../../../ui/components/Utils/gizmoUtil.js";
import { DEFAULT_LABEL_FONT } from "./labelFontOptions.js";
import { keepTransformControlsVisible } from "./transformControlsOverlay.js";
import {
  getBackboardPositionFromTransformControlsWorldPosition,
  getBackboardTransformControlsWorldPosition,
} from "./worldLabelTransformAnchor.js";
const DEFAULT_TEXT_FONT_SIZE = 90;
const DEFAULT_TEXT_FONT = `${DEFAULT_TEXT_FONT_SIZE}px ${DEFAULT_LABEL_FONT}`;

function getFontSizeFromFont(font) {
  const fontMatch = String(font || "").match(/(\d+(?:\.\d+)?)px/);
  const fontSize = fontMatch ? Number(fontMatch[1]) : NaN;
  return !isNaN(fontSize) && fontSize > 0 ? fontSize : DEFAULT_TEXT_FONT_SIZE;
}

function ensureIndependentTextSize(style) {
  if (!style) return;

  if (!Object.prototype.hasOwnProperty.call(style, "font")) {
    style.font = DEFAULT_TEXT_FONT;
  }

  if (!Object.prototype.hasOwnProperty.call(style, "fontSize")) {
    style.fontSize = getFontSizeFromFont(style.font);
  }
}

class WorldLabel {
  constructor(options) {
    this.id = options.id; // Label ID
    this.type = options.type; // Label Type (used for 3D or 2D)
    this.content = options.content; // Label Text Array
    this.renderLimits = options.renderLimits; // Array with the labels renderlimits
    this.visibleStart = false;

    if (Object.prototype.hasOwnProperty.call(options, "pointerVisible")) {
      this.pointerIntendedVisible = Boolean(options.pointerVisible);
    }


    if (Object.prototype.hasOwnProperty.call(options, "visibleStart")) {
      this.visibleStart = options.visibleStart;
    }

    const anchorPosition = options.anchorPosition;

    const rotation = options.rotation;

    const style = options.content.style;
    ensureIndependentTextSize(style);

    let backboardOffset = { x: 0, y: 0, z: 0 };
    if (Object.prototype.hasOwnProperty.call(options, "offset")) {
      backboardOffset = { x: options.offset.x, y: options.offset.y, z: options.offset.z };
    } else if (Object.prototype.hasOwnProperty.call(options, "offsetX")) {
      backboardOffset = { x: options.offsetX, y: options.offsetY, z: 0 };
    }

    this.settings = {
      depthTest: true,
      showMarker: true,
      autoRotate: false,
      autoScale: false,
      maxScale: 10,
      minScale: 1,
      baseScale: 0.0001,
      markerHoverSize: 15,
      markerInactiveSize: 10,
      markerInactiveBackground: "rgba(255,255,255,0.7)",
      markerHoverBackground: "rgba(255,255,255,0.9)",
      markerOpacity: 1.0,
      pointerThickness: "medium",
    };

    // Support for legacy label JSON where the keys were stored straight under the label instead of .settings
    for (const key in this.settings) {
      if (!Object.prototype.hasOwnProperty.call(this.settings, key)) continue;
      if (Object.prototype.hasOwnProperty.call(options, key)) {
        this.settings[key] = options[key];
      }
    }

    // Support for new settings property
    if (Object.prototype.hasOwnProperty.call(options, "settings")) {
      for (const key in this.settings) {
        if (!Object.prototype.hasOwnProperty.call(this.settings, key)) continue;
        if (Object.prototype.hasOwnProperty.call(options.settings, key)) {
          this.settings[key] = options.settings[key];
        }
      }
    }

    // World labels keep a fixed physical size, including legacy scenes.
    this.settings.autoScale = false;

    this.userHidden = true;

    this.labelRenderer = createWorldLabelRenderer({ content: this.content, style });
    this.canvasManager = this.labelRenderer.getCanvasManager(); // Legacy alias for callers that still inspect the canvas renderer directly.
    this.backboard = this.labelRenderer; // Keep labels.js interop behind the renderer boundary.
    this.canvasTexture = this.labelRenderer.getTexture(); // Legacy alias for callers that still inspect the texture directly.

    var plane = this.labelRenderer.getMesh();
    if (plane.material) {
      plane.material.depthTest = this.settings.depthTest;
    }
    if (this.labelRenderer.setDepthTest) {
      this.labelRenderer.setDepthTest(this.settings.depthTest);
    }

    this.meshManager = new LabelMesh({
      backgroundColor: style.backgroundColor,
      depthTest: this.settings.depthTest,
      pointerThickness: this.settings.pointerThickness,
    });

    const labelMesh = this.meshManager.getMesh();

    labelMesh.visible = false; // Important. labelMesh.visible needs to be false until the backboard loads or the backboard won't appear. Weird bug!?
    // The Label will load in the canvas.render callback at the bottom of this constructor

    this.pointerIntendedVisible = true;

    labelMesh.position.set(anchorPosition.x, anchorPosition.y, anchorPosition.z);
    plane.position.copy(backboardOffset);
    labelMesh.add(plane);
    this.meshManager.updatePointer();

    // Apply Rotation
    if (Array.isArray(rotation)) {
      labelMesh.quaternion.fromArray(rotation);
    } else if (rotation !== undefined) {
      // legacy
      if (!isNaN(rotation.x)) {
        labelMesh.rotation.x = (rotation.x * Math.PI) / 180;
      }

      if (!isNaN(rotation.y)) {
        labelMesh.rotation.y = (rotation.y * Math.PI) / 180;
      }

      if (!isNaN(rotation.z)) {
        labelMesh.rotation.z = (rotation.z * Math.PI) / 180;
      }
    }

    // Apply Scale
    let scale = 1;
    if (options.scale !== undefined) {
      scale = options.scale;
    }

    labelMesh.scale.setScalar(scale);

    // Add Mesh to Scene
    store.scene.add(labelMesh);

    let markerOpacity = 1.0;

    // If we don't want to see the marker set the opacity to 0.0;
    if (!this.settings.showMarker) {
      markerOpacity = 0.0;
    }

    const markerOptions = {
      hoverSize: this.settings.markerHoverSize,
      inactiveSize: this.settings.markerInactiveSize,
      opacity: markerOpacity,
      inactiveBackground: this.settings.markerInactiveBackground,
      hoverBackground: this.settings.markerHoverBackground,
    };

    this.marker = markers.create(markerOptions);

    this.marker.setWorldPosition(anchorPosition);
    options.renderLimits.map((limit) => {
      this.marker.addRenderLimit(limit);
    });
    // If showing the marker then add the callbacks else don't
    if (this.settings.showMarker) {
      this.handleClick = this.handleClick.bind(this);
      this.marker.addClickCallback(this.handleClick);
    }

    // Check if this is a problem and should be moved to inside above
    this.visibilityChange = this.visibilityChange.bind(this);
    this.marker.addVisibilityCallback(this.visibilityChange);

    // Render returns a promise due to async loading of background images.
    // Need to set the billboard scale once we know the height of the image as that updates the canvas background size
    this.renderBackboard().then(() => {
      if (this.meshManager) {
        this.meshManager.updatePointer();
        this.updatePointerRespectingVisibility();
      }
      // Once the canvas has loaded it's safe to set it to visible.
      // If done earlier it may cause a bug where the backboard won't load.
      if (this.visibleStart) {
        this.setVisible(true);
      }
    });
  }

  setPointerThickness(thickness) {
    this.settings.pointerThickness = thickness;
    this.meshManager.setPointerThickness(thickness);
    this.meshManager.updatePointer();
    store.requestRender();
  }

  handleClick() {
    const labelMesh = this.meshManager.getMesh();
    if (this.userHidden) {
      labelMesh.visible = true;
      this.userHidden = false;
    } else {
      labelMesh.visible = false;
      this.userHidden = true;
    }
  }

  handleRotate() {
    if (this.settings.autoRotate) {
      const labelMesh = this.meshManager.getMesh();
      labelMesh.lookAt(store.camera.position.x, labelMesh.position.y, store.camera.position.z); // Rotate around Y only
      // this.label.lookAt(store.camera.position);//Rotate all
    }
  }

  setVisible(isVisible) {
    const labelMesh = this.meshManager.getMesh();
    this.userHidden = !isVisible;
    labelMesh.visible = isVisible;
  }

  isVisible() {
    const labelMesh = this.meshManager.getMesh();
    return labelMesh.visible;
  }

  visibilityChange(isVisible) {
    const labelMesh = this.meshManager.getMesh();
    if (!isVisible && labelMesh.visible) {
      labelMesh.visible = false;
    } else if (isVisible && !labelMesh.visible) {
      if (!this.userHidden) {
        labelMesh.visible = true;
      }
    }
  }

  renderBackboard() {
    if (this.labelRenderer?.render) {
      return this.labelRenderer.render();
    }

    if (this.canvasManager?.render) {
      return this.canvasManager.render().then(() => {
        const canvas = this.canvasManager.getCanvas();
        const backboard = this.meshManager.getMesh().children[0];
        backboard.scale.set(canvas.width / 200, canvas.height / 200, 1);
        backboard.updateMatrixWorld();
        backboard.material.needsUpdate = true;
        if (this.canvasTexture) {
          this.canvasTexture.needsUpdate = true;
        }
      });
    }

    return Promise.resolve();
  }

  update() {
    return this.renderBackboard().then(() => {
      const updatePointerRespectingVisibility = this.updatePointerRespectingVisibility || WorldLabel.prototype.updatePointerRespectingVisibility;
      if (this.meshManager && updatePointerRespectingVisibility) {
        updatePointerRespectingVisibility.call(this);
      }
      store.requestRender();
    });
  }

  setMarkerStyle(style) {
    const markerStyle = { ...style };

    if (Object.prototype.hasOwnProperty.call(style, "showMarker")) this.settings.showMarker = style.showMarker;
    if (Object.prototype.hasOwnProperty.call(style, "markerHoverSize") && !Object.prototype.hasOwnProperty.call(style, "hoverSize")) {
      markerStyle.hoverSize = style.markerHoverSize;
    }
    if (Object.prototype.hasOwnProperty.call(style, "markerInactiveSize") && !Object.prototype.hasOwnProperty.call(style, "inactiveSize")) {
      markerStyle.inactiveSize = style.markerInactiveSize;
    }
    if (
      Object.prototype.hasOwnProperty.call(style, "markerInactiveBackground") &&
      !Object.prototype.hasOwnProperty.call(style, "inactiveBackground")
    ) {
      markerStyle.inactiveBackground = style.markerInactiveBackground;
    }
    if (Object.prototype.hasOwnProperty.call(style, "markerHoverBackground") && !Object.prototype.hasOwnProperty.call(style, "hoverBackground")) {
      markerStyle.hoverBackground = style.markerHoverBackground;
    }

    if (Object.prototype.hasOwnProperty.call(markerStyle, "hoverSize")) this.settings.markerHoverSize = markerStyle.hoverSize;
    if (Object.prototype.hasOwnProperty.call(markerStyle, "inactiveSize")) this.settings.markerInactiveSize = markerStyle.inactiveSize;
    if (Object.prototype.hasOwnProperty.call(markerStyle, "inactiveBackground"))
      this.settings.markerInactiveBackground = markerStyle.inactiveBackground;
    if (Object.prototype.hasOwnProperty.call(markerStyle, "hoverBackground")) this.settings.markerHoverBackground = markerStyle.hoverBackground;
    this.marker.updateStyle(markerStyle);
  }

  replaceText(textArray, richTextModel) {
    this.content.text = textArray;
    if (richTextModel) {
      this.content.richText = JSON.parse(JSON.stringify(richTextModel));
    } else {
      delete this.content.richText;
    }
    if (this.labelRenderer?.replaceText) {
      this.labelRenderer.replaceText(textArray, richTextModel);
    } else if (this.canvasManager?.replaceText) {
      this.canvasManager.replaceText(textArray, richTextModel);
    }
    this.update();
  }

  addText(textLine) {
    delete this.content.richText;
    if (this.labelRenderer?.addText) {
      this.labelRenderer.addText(textLine);
      this.content.text = this.labelRenderer.textArray;
    } else if (this.canvasManager?.addText) {
      this.canvasManager.addText(textLine);
      this.content.text = this.canvasManager.textArray;
    }
    this.update();
  }

  editText(textID, textLine) {
    delete this.content.richText;
    if (this.labelRenderer?.editText) {
      this.labelRenderer.editText(textID, textLine);
      this.content.text = this.labelRenderer.textArray;
    } else if (this.canvasManager?.editText) {
      this.canvasManager.editText(textID, textLine);
      this.content.text = this.canvasManager.textArray;
    }
    this.update();
  }

  deleteImage() {
    delete this.content.style.image;
    delete this.content.style.imagePosition;
    if (this.labelRenderer?.setStyle) {
      this.labelRenderer.setStyle({ image: "", imagePosition: undefined });
    } else if (this.canvasManager?.setStyle) {
      this.canvasManager.setStyle({ image: "", imagePosition: undefined });
    }
    this.update();
    store.requestRender();
  }

  setStyle(newStyle) {
    const labelMesh = this.meshManager.getMesh();
    const tStyle = Object.assign({}, this.content.style, newStyle);
    if (Object.prototype.hasOwnProperty.call(newStyle, "font")) {
      tStyle.fontSize = Object.prototype.hasOwnProperty.call(newStyle, "fontSize")
        ? Number(newStyle.fontSize)
        : getFontSizeFromFont(newStyle.font);
    }
    ensureIndependentTextSize(tStyle);
    if (this.labelRenderer?.setStyle) {
      this.labelRenderer.setStyle(tStyle);
    } else if (this.canvasManager?.setStyle) {
      this.canvasManager.setStyle(tStyle);
    }

    if (Object.prototype.hasOwnProperty.call(tStyle, "backgroundColor")) {
      // Extract opacity from backgroundColor (if it's rgba)
      const bgColor = tStyle.backgroundColor;
      labelMesh.material.color.setStyle(bgColor);

      // Handle opacity if backgroundColor contains alpha
      if (bgColor.includes("rgba")) {
        const rgbaMatch = bgColor.match(/rgba?\(([^)]+)\)/);
        if (rgbaMatch) {
          const values = rgbaMatch[1].split(",").map((v) => parseFloat(v.trim()));
          if (values.length === 4) {
            const opacity = values[3];
            labelMesh.material.opacity = opacity;
            labelMesh.material.transparent = opacity < 1;
          }
        }
      } else {
        // Reset to full opacity for non-rgba colors
        labelMesh.material.opacity = 1;
        labelMesh.material.transparent = false;
      }
    }

    this.content.style = tStyle;
    const updatePromise = this.update();
    store.requestRender();
    return updatePromise;
  }

  modifyScale(newScale) {
    const labelMesh = this.meshManager.getMesh();
    const oldScale = store.globalscale;
    labelMesh.position.sub(store.inventumTransform); // Remove the Inventum Transform temp
    labelMesh.position.divideScalar(oldScale); // Calculate what the original 100% was
    labelMesh.position.multiplyScalar(newScale); // Calculate the new scale
    labelMesh.position.add(store.inventumTransform); // Add the Inventum Transform back
  }

  modifyTransform(deltaTransform) {
    const labelMesh = this.meshManager.getMesh();
    labelMesh.position.add(deltaTransform);
  }

  remove() {
    const labelMesh = this.meshManager.getMesh();
    if (this.labelRenderer?.dispose) {
      this.labelRenderer.dispose();
    }
    store.scene.remove(labelMesh);
    store.sceneHUD.remove(this.marker.sprite);
  }

  updateOffsetControls() {
    const pointerMesh = this.meshManager.getMesh();
    pointerMesh.updateMatrixWorld();
    const backboard = pointerMesh.children[0];
    this._offsetControlsHelperCube.position.copy(getBackboardTransformControlsWorldPosition(pointerMesh, backboard));
    store.requestRender();
  }

  toggleOffsetControls() {
    if (this._offsetControls) {
      cleanupGizmo(this);
      return;
    }

    this._offsetControls = keepTransformControlsVisible(new TransformControls(store.camera, store.webglRenderer.domElement));
    this._offsetControls.addEventListener("dragging-changed", function (event) {
      store.controls.enabled = !event.value;
    });

    this._offsetControls.addEventListener("change", (e) => {
      backboard.position.copy(
        getBackboardPositionFromTransformControlsWorldPosition(pointerMesh, backboard, this._offsetControlsHelperCube.position)
      );
      this.updatePointerRespectingVisibility();
      store.requestRender();
    });

    var tGeom = new THREE.BoxGeometry(1, 1, 1);
    var tMat = new THREE.MeshBasicMaterial({ color: 0xff0000 });
    this._offsetControlsHelperCube = new THREE.Mesh(tGeom, tMat);

    const pointerMesh = this.meshManager.getMesh();
    pointerMesh.updateMatrixWorld();
    const backboard = pointerMesh.children[0];
    this._offsetControlsHelperCube.position.copy(getBackboardTransformControlsWorldPosition(pointerMesh, backboard));
    this._offsetControlsHelperCube.visible = false;
    store.scene.add(this._offsetControlsHelperCube);

    // There's a weird error where the offset in the z plane gets reported as 3.000e15 if the z axis is in direct aligment with the parent object.
    // It needs to be offset by a tiny amount to fix it. Should only need to be done once when the helper cube is created the first time.
    this._offsetControlsHelperCube.position.z += 0.001;

    this._offsetControls.attach(this._offsetControlsHelperCube);
    store.scene.add(this._offsetControls);
  }

  toggleAutoRotate() {
    this.settings.autoRotate = !this.settings.autoRotate;
  }

  toggleAutoScale() {
    // Compatibility no-op: camera-driven sizing is no longer supported.
  }

  addRenderLimit(limit) {
    this.renderLimits.push(limit);
    this.marker.addRenderLimit(limit);
  }

  deleteRenderLimit(limitID) {
    this.renderLimits.splice(limitID, 1);
    this.marker.deleteRenderLimit(limitID);
  }

  updateRenderLimit(limitID, updatedValue) {
    this.renderLimits[limitID].value = updatedValue;
    this.marker.updateRenderLimit(limitID, updatedValue);
  }

  serialize() {
    const labelMesh = this.meshManager.getMesh();
    const tempPos = labelMesh.position; // this.marker.getWorldPosition
    let backboardOffset = this.meshManager.getBackboardPosition();

    const anchorPosition = { x: +tempPos.x.toFixed(2), y: +tempPos.y.toFixed(2), z: +tempPos.z.toFixed(2) };
    backboardOffset = { x: +backboardOffset.x.toFixed(2), y: +backboardOffset.y.toFixed(2), z: +backboardOffset.z.toFixed(2) };

    const tempSerialize = {
      id: this.id,
      offset: backboardOffset,
      type: this.type,
      content: JSON.parse(JSON.stringify(this.content)),
      renderLimits: JSON.parse(JSON.stringify(this.renderLimits)),
      anchorPosition: anchorPosition,
      scale: this.meshManager.getScale(),
      rotation: this.meshManager.getRotation(),
      settings: JSON.parse(JSON.stringify(this.settings)),
      visibleStart: this.visibleStart,
      pointerVisible: this.pointerIntendedVisible,
    };

    return tempSerialize;
  }

  setPointerVisible(isVisible) {
    this.pointerIntendedVisible = isVisible;
    const labelMesh = this.meshManager.getMesh();
    // labelMesh.visible = true;
    const geometry = labelMesh.geometry;
    const positions = geometry.attributes.position;

    if (!isVisible) {
      for (let i = 0; i < positions.array.length; i++) {
        positions.array[i] = 0;
      }
    } else {
      this.meshManager.updatePointer();
    }

    positions.needsUpdate = true;
    store.requestRender();
  }

  updatePointerRespectingVisibility() {
    if (this.pointerIntendedVisible) {
      this.meshManager.updatePointer();
    } else {
      const labelMesh = this.meshManager.getMesh();
      const geometry = labelMesh.geometry;
      const positions = geometry.attributes.position;
      for (let i = 0; i < positions.array.length; i++) {
        positions.array[i] = 0;
      }
      positions.needsUpdate = true;
    }
  }
}

export { WorldLabel };
